import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { config } from '../config';
import UserMenu from './UserMenu';
import './AdminPanel.css';

interface User {
  userId: string;
  email: string;
  name: string;
  role: 'admin' | 'user';
  created: string;
  lastLogin?: string;
  status: 'active' | 'inactive';
}

interface Company {
  companyId: string;
  companyName: string;
  domain: string;
  customDomain?: string;
  allowedEmails: string[];
  created: string;
  lastUpdated: string;
  status: 'active' | 'inactive';
  adminUserId: string;
}

interface AdminPanelState {
  company: Company | null;
  users: User[];
  currentUser: User | null;
  isLoading: boolean;
  error: string;
  showAddUserModal: boolean;
  showDeleteModal: boolean;
  userToDelete: User | null;
  newUserEmail: string;
  totalUsers: number;
  adminCount: number;
  userCount: number;
}

interface AdminPanelProps {
  companyIdProp?: string;
}

const AdminPanel: React.FC<AdminPanelProps> = ({ companyIdProp }) => {
  const { company: companyIdFromUrl } = useParams<{ company: string }>();
  const companyId = companyIdProp || companyIdFromUrl;
  const navigate = useNavigate();

  const [state, setState] = useState<AdminPanelState>({
    company: null,
    users: [],
    currentUser: null,
    isLoading: true,
    error: '',
    showAddUserModal: false,
    showDeleteModal: false,
    userToDelete: null,
    newUserEmail: '',
    totalUsers: 0,
    adminCount: 0,
    userCount: 0,
  });

  useEffect(() => {
    if (companyId) {
      loadDashboardData();
    }
  }, [companyId]);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('dashzap_token');
    return {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    };
  };

  const loadDashboardData = async (skipCache = false) => {
    try {
      console.log('🔄 Loading dashboard data for companyId:', companyId);

      // Check cache first (5-minute cache)
      const cacheKey = `users_${companyId}`;
      const cached = localStorage.getItem(cacheKey);
      if (!skipCache && cached) {
        try {
          const { data, timestamp } = JSON.parse(cached);
          const age = Date.now() - timestamp;
          if (age < 5 * 60 * 1000) { // 5 minutes
            console.log('✅ Using cached users (age: ' + Math.round(age / 1000) + 's)');
            setState(prev => ({
              ...prev,
              company: data.company,
              users: data.users || [],
              totalUsers: data.totalUsers || 0,
              adminCount: data.adminCount || 0,
              userCount: data.userCount || 0,
              isLoading: false,
            }));

            // Set current user from JWT token
            const token = localStorage.getItem('dashzap_token');
            if (token) {
              try {
                const payload = JSON.parse(atob(token.split('.')[1]));
                const currentUser = data.users?.find((user: User) => user.userId === payload.userId);
                setState(prev => ({ ...prev, currentUser }));
              } catch (error) {
                console.error('Error parsing JWT token:', error);
              }
            }
            return;
          }
        } catch (e) {
          console.log('Cache invalid, fetching fresh data');
        }
      }

      console.log('📡 API URL:', `${config.API_BASE_URL}/admin/${companyId}`);
      const startTime = Date.now();

      setState(prev => ({ ...prev, isLoading: true, error: '' }));

      const response = await fetch(`${config.API_BASE_URL}/admin/${companyId}`, {
        method: 'GET',
        headers: getAuthHeaders(),
      });

      const endTime = Date.now();
      console.log(`⏱️ API call took ${endTime - startTime}ms`);

      if (response.status === 401) {
        localStorage.removeItem('dashzap_token');
        navigate('/onboarding');
        return;
      }

      const data = await response.json();

      if (response.ok) {
        console.log('✅ Dashboard data loaded:', data.users?.length || 0, 'users');

        // Cache the result
        localStorage.setItem(cacheKey, JSON.stringify({
          data,
          timestamp: Date.now()
        }));

        setState(prev => ({
          ...prev,
          company: data.company,
          users: data.users || [],
          totalUsers: data.totalUsers || 0,
          adminCount: data.adminCount || 0,
          userCount: data.userCount || 0,
          isLoading: false,
        }));

        // Set current user from JWT token
        const token = localStorage.getItem('dashzap_token');
        if (token) {
          try {
            const payload = JSON.parse(atob(token.split('.')[1]));
            const currentUser = data.users?.find((user: User) => user.userId === payload.userId);
            setState(prev => ({ ...prev, currentUser }));
          } catch (error) {
            console.error('Error parsing JWT token:', error);
          }
        }
      } else {
        console.error('❌ API call failed:', response.status, data.error);
        setState(prev => ({
          ...prev,
          error: data.error || 'Failed to load dashboard data',
          isLoading: false,
        }));
      }
    } catch (error: any) {
      console.error('❌ Error loading dashboard data:', error);
      setState(prev => ({
        ...prev,
        error: 'Network error',
        isLoading: false,
      }));
    }
  };

  const handleAddUser = async () => {
    if (!state.newUserEmail.trim()) {
      setState(prev => ({ ...prev, error: 'Email is required' }));
      return;
    }

    setState(prev => ({ ...prev, isLoading: true, error: '' }));

    try {
      const response = await fetch(`${config.API_BASE_URL}/admin/add-user/${companyId}`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ email: state.newUserEmail.trim() }),
      });

      const data = await response.json();

      if (response.ok) {
        setState(prev => ({
          ...prev,
          showAddUserModal: false,
          newUserEmail: '',
          isLoading: false,
        }));

        // Reload dashboard data (skip cache to get fresh data)
        await loadDashboardData(true);
      } else {
        setState(prev => ({
          ...prev,
          error: data.error || 'Failed to add user',
          isLoading: false,
        }));
      }
    } catch (error) {
      setState(prev => ({
        ...prev,
        error: 'Network error',
        isLoading: false,
      }));
    }
  };

  const handleDeleteUser = async () => {
    if (!state.userToDelete) return;

    setState(prev => ({ ...prev, isLoading: true, error: '' }));

    try {
      const response = await fetch(`${config.API_BASE_URL}/admin/remove-user/${companyId}`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
        body: JSON.stringify({ userId: state.userToDelete.userId }),
      });

      const data = await response.json();

      if (response.ok) {
        setState(prev => ({
          ...prev,
          showDeleteModal: false,
          userToDelete: null,
          isLoading: false,
        }));

        // Reload dashboard data (skip cache to get fresh data)
        await loadDashboardData(true);
      } else {
        setState(prev => ({
          ...prev,
          error: data.error || 'Failed to remove user',
          isLoading: false,
        }));
      }
    } catch (error) {
      setState(prev => ({
        ...prev,
        error: 'Network error',
        isLoading: false,
      }));
    }
  };


  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const formatDateTime = (dateString: string) => {
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const openAddUserModal = () => {
    setState(prev => ({
      ...prev,
      showAddUserModal: true,
      newUserEmail: '',
      error: ''
    }));
  };

  const closeAddUserModal = () => {
    setState(prev => ({
      ...prev,
      showAddUserModal: false,
      newUserEmail: '',
      error: ''
    }));
  };

  const openDeleteModal = (user: User) => {
    setState(prev => ({
      ...prev,
      showDeleteModal: true,
      userToDelete: user,
      error: ''
    }));
  };

  const closeDeleteModal = () => {
    setState(prev => ({
      ...prev,
      showDeleteModal: false,
      userToDelete: null,
      error: ''
    }));
  };

  if (state.isLoading && !state.company) {
    return (
      <div className="admin-panel">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading dashboard...</p>
        </div>
      </div>
    );
  }

  if (!state.company) {
    return (
      <div className="admin-panel">
        <div className="error-container">
          <h2>Company Not Found</h2>
          <p>The company you're looking for doesn't exist or you don't have access to it.</p>
          <button onClick={() => navigate('/')} className="btn btn-primary">
            Go Home
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-panel">
      {/* Main Content */}
      <main className="admin-main" style={{ paddingTop: '1rem' }}>
        {state.error && (
          <div className="error-message">
            {state.error}
          </div>
        )}

        {/* Compact Company Info */}
        <div style={{ marginBottom: '1.5rem', padding: '0.75rem 1rem', background: '#f9fafb', borderRadius: '0.5rem', display: 'flex', alignItems: 'center', gap: '1.5rem', flexWrap: 'wrap' }}>
          <div>
            <span style={{ fontSize: '1.25rem', fontWeight: '600', color: '#111827' }}>{state.company.companyName}</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: '#6b7280', fontSize: '0.875rem' }}>
            <span>🌐</span>
            <span>{state.company.domain}</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: '#6b7280', fontSize: '0.875rem' }}>
            <span>📅</span>
            <span>{formatDate(state.company.created)}</span>
          </div>
          <div>
            <span className={`status-badge ${state.company.status}`}>
              {state.company.status}
            </span>
          </div>
        </div>

        {/* Users Management */}
        <div className="section">
          <div className="section-header">
            <h2>Users Management</h2>
            <button onClick={openAddUserModal} className="btn btn-primary">
              Add User
            </button>
          </div>

          <div className="users-table-container">
            <table className="users-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Role</th>
                  <th>Created</th>
                  <th>Last Login</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {state.users.map((user) => (
                  <tr key={user.userId}>
                    <td>{user.name}</td>
                    <td>{user.email}</td>
                    <td>
                      <span className={`role-badge ${user.role}`}>
                        {user.role}
                      </span>
                    </td>
                    <td>{formatDate(user.created)}</td>
                    <td>{user.lastLogin ? formatDateTime(user.lastLogin) : 'Never'}</td>
                    <td>
                      <span className={`status-badge ${user.status}`}>
                        {user.status}
                      </span>
                    </td>
                    <td>
                      {user.userId !== state.currentUser?.userId && (
                        <button
                          onClick={() => openDeleteModal(user)}
                          className="btn btn-danger btn-sm"
                          disabled={state.isLoading}
                        >
                          Remove
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {state.users.length === 0 && (
              <div className="empty-state">
                <div className="empty-icon">👥</div>
                <h3>No Users Found</h3>
                <p>Add your first user to get started.</p>
              </div>
            )}
          </div>
        </div>
      </main>

      {/* Add User Modal */}
      {state.showAddUserModal && (
        <div className="modal-overlay" onClick={closeAddUserModal}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Add New User</h3>
              <button onClick={closeAddUserModal} className="modal-close">×</button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label htmlFor="user-email">Email Address</label>
                <input
                  id="user-email"
                  type="email"
                  value={state.newUserEmail}
                  onChange={(e) => setState(prev => ({ ...prev, newUserEmail: e.target.value }))}
                  placeholder="user@company.com"
                  className="form-input"
                  onKeyPress={(e) => e.key === 'Enter' && handleAddUser()}
                />
                <small>
                  This email will be added to the allowed list. The user can then sign in with Google.
                </small>
              </div>
            </div>
            <div className="modal-actions">
              <button onClick={closeAddUserModal} className="btn btn-secondary">
                Cancel
              </button>
              <button
                onClick={handleAddUser}
                className="btn btn-primary"
                disabled={state.isLoading || !state.newUserEmail.trim()}
              >
                {state.isLoading ? 'Adding...' : 'Add User'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete User Modal */}
      {state.showDeleteModal && state.userToDelete && (
        <div className="modal-overlay" onClick={closeDeleteModal}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Remove User</h3>
              <button onClick={closeDeleteModal} className="modal-close">×</button>
            </div>
            <div className="modal-body">
              <p>
                Are you sure you want to remove <strong>{state.userToDelete.name}</strong> ({state.userToDelete.email})?
              </p>
              <div className="warning-box">
                <strong>⚠️ Warning:</strong> This will deactivate the user's account.
                They will no longer be able to access the application, but their data will be preserved.
              </div>
            </div>
            <div className="modal-actions">
              <button onClick={closeDeleteModal} className="btn btn-secondary">
                Cancel
              </button>
              <button
                onClick={handleDeleteUser}
                className="btn btn-danger"
                disabled={state.isLoading}
              >
                {state.isLoading ? 'Removing...' : 'Remove User'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminPanel;